// src/hooks/useRefCheck.js
import { useEffect, useState, useCallback } from 'react';

// Module-level cache survives React StrictMode remounts
const cache = {
  refId: null,
  promise: null,
  result: null,
};

// Background retry interval when validation has failed but the kiosk is still
// allowing the user to submit. Once validation succeeds the form silently
// picks up branding/label/washrooms.
const RETRY_INTERVAL_MS = 30 * 1000;

// Terminal error codes — retrying won't change the answer, so don't poll.
// MISSING_REFID:  no refId in URL.
// INVALID_REFID:  asset doesn't exist on TB (e.g. typo'd QR like …538bee).
// ID_MISMATCH:    upstream returned a different id than we asked for.
// Exported so App.js can render the fatal-error card for these codes
// (and skip the "Reconnecting…" banner — we're not reconnecting).
export const TERMINAL_REF_CODES = new Set(['MISSING_REFID', 'INVALID_REFID', 'ID_MISMATCH']);

export function useRefCheck(refId, token = null) {
  const [state, setState] = useState(() => {
    if (cache.refId === refId && cache.result) {
      return cache.result;
    }
    return { status: 'loading' };
  });
  // Bumped by retry() to force the effect to re-run and bypass the cache.
  const [retryNonce, setRetryNonce] = useState(0);

  const retry = useCallback(() => {
    cache.refId = null;
    cache.promise = null;
    cache.result = null;
    setState({ status: 'loading' });
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!refId) {
      console.log('[refCheck] no refId found in URL');
      setState({ status: 'error', code: 'MISSING_REFID' });
      return;
    }

    if (cache.refId === refId && cache.result) {
      setState(cache.result);
      return;
    }

    if (cache.refId === refId && cache.promise) {
      cache.promise.then((result) => setState(result));
      return;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || '';
    let url = `${API_BASE}/api/ref/validate?refId=${encodeURIComponent(refId)}`;
    if (token) url += `&token=${encodeURIComponent(token)}`;

    console.log('[refCheck] ➜ GET', url);

    cache.refId = refId;
    cache.promise = fetch(url)
      .then(async (r) => {
        console.log('[refCheck] ⇦ status', r.status);
        let body = {};
        try {
          body = await r.json();
        } catch (e) {
          console.log('[refCheck] (no JSON body)', e?.message);
        }
        // The body carries Service Desk device access tokens (serviceDesks[].token);
        // the kiosk forwards console output to its device logs, so mask them.
        console.log('[refCheck] ⇦ body', Array.isArray(body?.data?.serviceDesks)
          ? { ...body, data: { ...body.data, serviceDesks: body.data.serviceDesks.map((d) => ({ ...d, token: d?.token ? '[redacted]' : d?.token })) } }
          : body);

        const tbToken = r.headers.get('X-TB-Token');
        const tbRefreshToken = r.headers.get('X-TB-Refresh-Token');
        if (tbToken) {
          console.log('[refCheck] ⇦ X-TB-Token received');
        }
        if (tbRefreshToken) {
          console.log('[refCheck] ⇦ X-TB-Refresh-Token received');
        }

        let result;
        if (body?.ok) {
          // Honest "ref validated, we can serve a session" marker. The kiosk
          // (MainActivity.onConsoleMessage) hides its offline overlay on this.
          // It must fire on validation success regardless of how the token is
          // delivered: the token-gated path sets an X-TB-Token response header,
          // but the inline-branding path (per-customer theming) returns the
          // token INLINE in body.serviceDesks[] with NO header — so the kiosk
          // can't rely on the header alone. ASCII-only string (no Unicode) so
          // the native substring match can't be tripped by encoding.
          console.log('[refCheck] valid-session');
          result = {
            status: 'ok',
            data: {
              ...body.data,
              ...(tbToken && { tbToken }),
              ...(tbRefreshToken && { tbRefreshToken }),
            },
          };
        } else {
          result = { status: 'error', code: body?.code || 'INVALID_REFID' };
        }

        cache.result = result;
        console.log('[refCheck] done');
        return result;
      })
      .catch((err) => {
        console.log('[refCheck] ✗ fetch error', err?.message || err);
        const result = { status: 'error', code: 'NETWORK' };
        cache.refId = null;
        cache.promise = null;
        cache.result = null;
        return result;
      });

    cache.promise.then((result) => setState(result));
  }, [refId, retryNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background retry: when status is 'error' and the code is recoverable
  // (transient upstream/network failures), poll the validator on a 30s tick
  // and on the browser `online` event. Terminal codes (see TERMINAL_REF_CODES)
  // are skipped — re-asking won't change the answer and just hammers n8n/TB.
  // `retry` has useCallback([]) deps so its identity is stable across renders.
  useEffect(() => {
    if (state.status !== 'error' || TERMINAL_REF_CODES.has(state.code)) return;
    const id = setInterval(retry, RETRY_INTERVAL_MS);
    const onOnline = () => retry();
    if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
    return () => {
      clearInterval(id);
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
    };
  }, [state.status, state.code, retry]);

  return { ...state, retry };
}
